import React, { useEffect, useState } from 'react';
import {
  Col,
  Form,
  Input,
  InputNumber,
  Layout,
  message,
  Row,
  Select,
  Spin,
  Typography,
} from 'antd';
import { debounce, get, identity, startCase } from 'lodash';

import { OverlayLoading } from 'components/overlay-loading';
import PdButton from 'components/pd-button';

import ProductServices from 'services/product';
import RentStockServices from 'services/rent-stock';

import { errHandling } from 'utils';
import { numberOnly } from 'utils/inputNumber.js';

import './style.scss';

const { Text, Title } = Typography;
const { Content } = Layout;
const { Option } = Select;

const FIELDS = {
  SKU: 'sku',
  STOCK: 'quantity',
  TITLE: 'product_id',
  VARIANT: 'variant_color',
  WAREHOUSE: 'warehouse_id',
};

export function StockCreateEdit({
  closeModal = identity,
  stock = {},
  warehouseList = [],
}) {
  const [form] = Form.useForm();
  // will be like {0: {}, 1: {}... length: colorVariants.length} to convert it to [{}, {}] using Array.from() in handleSubmit
  const [colorVariantField, setColorVariantField] = useState({});

  const [colorVariants, setColorVariants] = useState([]);

  // get from fetchProductStock for tracking form
  const [colorVariantsBreakdown, setColorVariantsBreakdown] = useState([]);

  const [disableNonVariantStock, setDisableNonVariantStock] = useState(false);
  const [disableButton, setDisableButton] = useState(false);
  const [fetchingProduct, setFetchingProduct] = useState(false);
  const [loadingButton, setLoadingButton] = useState(false);
  const [loadingPage, setLoadingPage] = useState(false);
  const [productList, setProductList] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState('');
  const [selectedWarehouse, setSelectedWarehouse] = useState('');
  const [value, setValue] = useState({});

  const {
    id: stockId = '',
    product_color = {},
    quantity = 0,
    warehouse = {},
    product = {},
  } = stock;
  const {
    id: productId = '',
    title: productTitle = '',
    sku: productNoColorSku = '',
  } = product;
  const { sku: productColorSku = '' } = product_color;
  const { id: warehouseId = '' } = warehouse;

  const formItemLayout = {
    labelCol: { span: 4 },
    wrapperCol: { span: 16, offset: 3 },
    labelAlign: 'left',
  };

  const disableSaveButton =
    hasErrors(form.getFieldsError()) || disableButton || disableNonVariantStock;
  const notFoundContent = fetchingProduct ? <Spin size="small" /> : null;

  function hasErrors(fieldsError) {
    return Object.keys(fieldsError).some((field) => fieldsError[field]);
  }

  async function onFinish(values) {
    setLoadingButton(true);
    try {
      const payload = { stock: { ...values } };

      // conditional if edit, create many color, or create no color
      if (stockId) {
        await RentStockServices.updateStock(stockId, payload);
      } else if (colorVariants.length) {
        const colorVariantFieldArr = Array.from(colorVariantField);
        await Promise.all(
          colorVariantFieldArr.map(
            async ({ product_color_id = '', quantity = 0 } = {}) => {
              if (product_color_id) {
                await RentStockServices.createStock({
                  stock: { ...values, product_color_id, quantity },
                });
              }
            }
          )
        );
      } else {
        await RentStockServices.createStock(payload);
      }
      message.success(
        stockId ? 'Successfully edit stock' : 'Successfully add new stock'
      );
      closeModal();
    } catch (err) {
      errHandling(err);
    } finally {
      setLoadingButton(false);
    }
  }

  function changeValue(key, e) {
    if (e) {
      setValue((prevState) => ({
        ...prevState,
        [key]: e,
      }));
    }
  }

  function handleOnChangeProduct(val) {
    changeValue(FIELDS.TITLE, val);

    // reset state
    changeValue(FIELDS.STOCK, '0');
    setColorVariantField({});

    fetchProductDetail(val);

    // tracking disable color
    fetchProductStock(val);
  }

  function handleOnChangeColorVariantFields(colorId, fieldIdx, quantity) {
    const fieldValue = { product_color_id: colorId, quantity };
    setColorVariantField((prev) => ({ ...prev, [fieldIdx]: fieldValue }));
  }

  async function filterDisabledColor({
    colorVariansArr = [],
    productId = '',
    warehouse = '',
  }) {
    setColorVariantField({ length: colorVariants.length });
    try {
      const colorVarians = await fetchProductDetail(productId);
      let numberOfDisabledColor = 0;
      const filterDisabledColor = colorVarians.map((color, idx) => {
        let variansIdx = -1;
        const { id: colorId } = color || {};
        for (let i = 0; i < colorVariansArr.length; i++) {
          const { warehouseId = '' } = colorVariansArr[i];
          if (warehouse && warehouseId === warehouse) {
            variansIdx = i;
            break;
          }
        }
        const { colors = [] } = colorVariansArr[variansIdx] || {};
        const colorFindResult = colors.find(
          ({ colorId: findId = '' }) => findId === colorId
        );

        if (colorFindResult && variansIdx >= 0) {
          const { quantity = 0 } = colorFindResult || {};
          setColorVariantField((prev) => ({ ...prev, [idx]: { quantity } }));
          numberOfDisabledColor++;
          return { ...color, disabled: true, quantity };
        }

        return { ...color };
      });
      setColorVariants(filterDisabledColor);

      // set disable submit
      if (
        !!numberOfDisabledColor &&
        numberOfDisabledColor === colorVarians.length
      ) {
        setDisableButton(true);
      } else {
        setDisableButton(false);
      }
    } catch (error) {
      errHandling(error);
    }
  }

  function handleOnChangeWarehouse(val) {
    setSelectedWarehouse(val);
    changeValue(FIELDS.WAREHOUSE, val);

    // disabled nonVariants
    const filterResult = colorVariantsBreakdown.find(
      ({ warehouseId = '', productId = '' }) =>
        warehouseId === val && productId === selectedProduct
    );
    if (filterResult) {
      setDisableNonVariantStock(true);
    } else {
      setDisableNonVariantStock(false);
    }

    // filter disabled colorVariants
    filterDisabledColor({
      colorVariansArr: colorVariantsBreakdown,
      productId: selectedProduct,
      warehouse: val,
    });
  }

  function calculateTotalStock() {
    const colorVariantFieldArr = Array.from(colorVariantField);
    const totalStock = colorVariantFieldArr.reduce(
      (accumulator, currentValue = {}) =>
        accumulator + get(currentValue, ['quantity'], 0),
      0
    );
    changeValue(FIELDS.STOCK, String(totalStock));
  }

  async function fetchProduct(q) {
    setFetchingProduct(true);
    try {
      const {
        data: { data = [] },
      } = await ProductServices.getProductList({
        status: 'active',
        q,
      });
      setProductList(data);
    } catch (error) {
      errHandling(error);
    } finally {
      setFetchingProduct(false);
    }
  }

  async function fetchProductDetail(productId) {
    setLoadingPage(true);
    try {
      const {
        data: { data = {} },
      } = await ProductServices.getProductDetail(productId);
      const { color_variants = [], sku = '' } = data;
      setColorVariants(color_variants);
      changeValue(FIELDS.SKU, sku);
      // for tracking in fetchProductStock
      return color_variants;
    } catch (error) {
      errHandling(error);
    } finally {
      setLoadingPage(false);
    }
  }

  async function fetchProductStock(productId) {
    setSelectedProduct(productId);
    try {
      const {
        data: { data = {} },
      } = await RentStockServices.getProductStock(productId);
      const { warehouse_stocks = [] } = data;
      const variants = warehouse_stocks.map(
        ({ warehouse: { id: warehouseId = '' } = {}, varians = [] } = {}) => {
          // disable for nonVariant stock
          if (warehouseId === selectedWarehouse) {
            setDisableNonVariantStock(true);
          } else {
            setDisableNonVariantStock(false);
          }

          const colors = varians.map(
            ({ color: { id: colorId } = {}, quantity = 0 }) => ({
              colorId,
              quantity,
            })
          );
          return {
            colors,
            productId,
            warehouseId,
          };
        }
      );
      setColorVariantsBreakdown(variants);

      // filter disabled color
      filterDisabledColor({
        colorVariansArr: variants,
        productId,
        warehouse: selectedWarehouse,
      });

      // filter disabled non Variant
    } catch (error) {
      errHandling(error);
    }
  }

  useEffect(() => {
    fetchProduct(productTitle);
    if (stockId) {
      setValue({
        [FIELDS.SKU]: productColorSku || productNoColorSku,
        [FIELDS.STOCK]: quantity,
        [FIELDS.TITLE]: productId,
        [FIELDS.WAREHOUSE]: warehouseId,
      });
      form.setFieldsValue({
        [FIELDS.WAREHOUSE]: warehouseId,
        [FIELDS.TITLE]: productId,
        [FIELDS.STOCK]: quantity,
      });
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (!stockId) {
      setColorVariantField((prev) => ({
        ...prev,
        length: colorVariants.length,
      }));
    }
    // eslint-disable-next-line
  }, [colorVariants]);

  useEffect(() => {
    if (!stockId) calculateTotalStock();
    // eslint-disable-next-line
  }, [colorVariantField]);

  return (
    <Content>
      {loadingPage && <OverlayLoading open={loadingPage} />}
      <Form form={form} className="pd-stock-form" onFinish={onFinish}>
        <div className="section-container">
          <Title level={4} className="pd-margin-bottom-lg">
            {stockId ? 'Edit' : 'Create'} Rent Stock
          </Title>
          <div className="inner-section">
            <Form.Item
              label="Warehouse"
              name={FIELDS.WAREHOUSE}
              {...formItemLayout}
              rules={[
                {
                  message: 'Please input Warehouse Name!',
                  required: true,
                },
              ]}>
              <Select
                placeholder="Choose warehouse"
                onChange={handleOnChangeWarehouse}
                disabled={stockId}>
                {warehouseList.map(({ id = '', title = '' }) => (
                  <Option key={id} value={id}>
                    {startCase(title)}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              label="Title"
              name={FIELDS.TITLE}
              {...formItemLayout}
              rules={[
                {
                  message: 'Please input product!',
                  required: true,
                },
              ]}>
              <Select
                placeholder="Input Product"
                filterOption={false}
                showSearch
                onChange={handleOnChangeProduct}
                onSearch={debounce(fetchProduct, 500)}
                notFoundContent={notFoundContent}
                disabled={stockId}>
                {productList.map(({ id = '', title = '' }) => (
                  <Option key={id} value={id}>
                    {startCase(title)}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            {(!colorVariants.length || stockId) && (
              <Form.Item label="SKU" {...formItemLayout}>
                <Input
                  value={value[FIELDS.SKU]}
                  onChange={changeValue.bind(this, FIELDS.SKU)}
                  disabled
                />
              </Form.Item>
            )}
            <Form.Item
              label="Total Stock"
              name={FIELDS.STOCK}
              {...formItemLayout}
              rules={[
                {
                  message: 'Please input stock!',
                  required: !colorVariants.length,
                },
              ]}
              initialValue={value[FIELDS.STOCK] || 0}>
              <InputNumber
                onChange={changeValue.bind(this, FIELDS.STOCK)}
                min={0}
                formatter={numberOnly}
                parser={numberOnly}
                disabled={!!colorVariants.length || disableNonVariantStock}
              />
            </Form.Item>
          </div>
        </div>

        {!!colorVariants.length && (
          <div className="section-container">
            <Title level={4} className="pd-margin-bottom-md">
              Variant
            </Title>
            <Row gutter={[0, 24]} className="inner-section">
              <Col lg={8}>
                <Text strong>Color</Text>
              </Col>
              <Col lg={9}>
                <Text strong>SKU</Text>
              </Col>
              <Col lg={{ span: 6, offset: 1 }}>
                <Text strong>Qty</Text>
              </Col>
              {colorVariants.map(
                (
                  {
                    color = '',
                    disabled = false,
                    hex_code = 'ffffff',
                    id = '',
                    quantity = 0,
                    sku = '',
                  },
                  idx
                ) => (
                  <Row key={id} className="pd-margin-bottom-md">
                    <Col lg={4}>
                      {!hex_code || hex_code === 'ffffff' ? (
                        <PdButton
                          className="pd-margin-right-md"
                          style={{
                            backgroundColor: `#${hex_code}`,
                            border: '1px solid gray',
                          }}
                        />
                      ) : (
                        <PdButton
                          className="pd-margin-right-md"
                          style={{ backgroundColor: `#${hex_code}` }}
                        />
                      )}
                    </Col>
                    <Col lg={4} className="pd-margin-top-sm">
                      <Text>{color}</Text>
                    </Col>
                    <Col lg={9}>
                      <Form.Item>
                        <Input value={sku} disabled />
                      </Form.Item>
                    </Col>
                    <Col lg={{ span: 2, offset: 1 }}>
                      <Form.Item>
                        <InputNumber
                          min={0}
                          formatter={numberOnly}
                          parser={numberOnly}
                          value={
                            quantity
                              ? quantity
                              : get(
                                  colorVariantField,
                                  [idx, 'quantity'],
                                  quantity
                                )
                          }
                          disabled={disabled}
                          onChange={handleOnChangeColorVariantFields.bind(
                            this,
                            id,
                            idx
                          )}
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                )
              )}
            </Row>
          </div>
        )}
        <div className="button-container">
          <Form.Item>
            <Row type="flex" justify="center">
              <PdButton
                className="w-half height-32"
                disabled={disableSaveButton}
                full
                htmlType="submit"
                loading={loadingButton}
                type="primary">
                Save
              </PdButton>
            </Row>
          </Form.Item>
        </div>
      </Form>
    </Content>
  );
}
